/**
 * أول لمسة تسويقية — تُحفظ مرة واحدة ويُعاد إرفاقها بأحداث لاحقة (مثل /success بعد بوابة الدفع).
 */
import { safeLocal } from './safeStorage'
import type { EHSource } from './tracking/event-tracker'

export const FIRST_TOUCH_STORAGE_KEY = 'eh_first_touch'

export type FirstTouchPayload = {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  initial_referrer?: string
  /** أول مسار كامل عند التقاط أول لمسة */
  landing_page_url?: string
  landing_pathname?: string
  captured_at: number
}

function parseUtmFromSearch(search: string): Partial<FirstTouchPayload> {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const out: Partial<FirstTouchPayload> = {}
  const u = (k: string) => {
    const v = q.get(k)
    return v && v.trim() ? v.trim() : undefined
  }
  out.utm_source = u('utm_source')
  out.utm_medium = u('utm_medium')
  out.utm_campaign = u('utm_campaign')
  out.utm_term = u('utm_term')
  out.utm_content = u('utm_content')
  return out
}

export function captureFirstTouchFromWindow(): void {
  if (typeof window === 'undefined') return
  try {
    const existing = safeLocal.getItem(FIRST_TOUCH_STORAGE_KEY)
    if (existing) {
      const parsed = JSON.parse(existing) as FirstTouchPayload
      if (parsed && parsed.captured_at) return
    }
  } catch {
    // نُعيد المحاولة بالتقاط جديد
  }

  const href = window.location.href
  const pathname = window.location.pathname || '/'
  const search = window.location.search || ''
  const utm = parseUtmFromSearch(search)
  const ref =
    typeof document !== 'undefined' && document.referrer && document.referrer.trim() !== ''
      ? document.referrer.trim()
      : undefined

  const hasUtm = !!(utm.utm_source || utm.utm_medium || utm.utm_campaign || utm.utm_term || utm.utm_content)

  if (!hasUtm && !ref) return

  const payload: FirstTouchPayload = {
    ...utm,
    initial_referrer: ref,
    landing_page_url: href,
    landing_pathname: pathname,
    captured_at: Date.now(),
  }

  try {
    safeLocal.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // تجاهل
  }
}

export function getFirstTouchPayload(): FirstTouchPayload | null {
  try {
    const raw = safeLocal.getItem(FIRST_TOUCH_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as FirstTouchPayload
  } catch {
    return null
  }
}

/** دمج أول لمسة في مصدر الحدث عند غياب القيم الحالية (مثلاً صفحة نجاح بدون UTM في الرابط) */
export function mergeFirstTouchIntoSource(source: EHSource): EHSource {
  const ft = getFirstTouchPayload()
  if (!ft) return source

  const merged = { ...source }
  if (!merged.utm_source && ft.utm_source) merged.utm_source = ft.utm_source
  if (!merged.utm_medium && ft.utm_medium) merged.utm_medium = ft.utm_medium
  if (!merged.utm_campaign && ft.utm_campaign) merged.utm_campaign = ft.utm_campaign
  if (!merged.utm_term && ft.utm_term) merged.utm_term = ft.utm_term
  if (!merged.utm_content && ft.utm_content) merged.utm_content = ft.utm_content

  if (!merged.referrer && ft.initial_referrer) {
    merged.referrer = ft.initial_referrer
  }

  return merged
}
